"use client";

import { Button } from "@heroui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@heroui/popover";
import clsx from "clsx";
import NextLink from "next/link";
import { useState } from "react";

import type { LanguageCode } from "@/config/i18n";
import { richText } from "@/components/legal/RichText";
import { sanitizeRedirectPath } from "@/lib/auth/redirect";
import { buildKakaoSignInHref } from "@/lib/auth/sign-in";
import { createTranslator } from "@/lib/i18n/translations";
import { setLanguageCookie } from "@/lib/languageCookie";
import { logWarning } from "@/lib/logger";
import { createClient } from "@/lib/supabase/client";

import OAuthProviderButton from "./OAuthProviderButton";

interface SignInModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentLang: LanguageCode;
  /** Same-origin path to return to after signing in (sanitized). */
  redirectPath?: string;
  buttonText?: string;
  buttonClassName?: string;
}

const noticeLinkClass =
  "rounded-small underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

export default function SignInModal({
  isOpen,
  onOpenChange,
  currentLang,
  redirectPath = "/",
  buttonText,
  buttonClassName,
}: SignInModalProps) {
  const t = createTranslator(currentLang);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const safeRedirectPath = sanitizeRedirectPath(redirectPath);

  const handleGoogleSignIn = async () => {
    // Remember the language so the post-login page matches.
    setLanguageCookie(currentLang);
    setHasError(false);
    setIsRedirecting(true);

    const callbackUrl = new URL("/auth/callback", window.location.origin);

    callbackUrl.searchParams.set("next", safeRedirectPath);

    const { error } = await createClient().auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: callbackUrl.toString() },
    });

    if (error) {
      logWarning("Could not start Google sign-in", { code: error.code });
      setIsRedirecting(false);
      setHasError(true);
    }
  };

  return (
    <Popover
      backdrop="transparent"
      isOpen={isOpen}
      placement="bottom-end"
      onOpenChange={onOpenChange}
    >
      {/* The trigger is compact on phones, where the navbar row is tight. */}
      <PopoverTrigger>
        <Button
          className={clsx("min-w-0 px-3 sm:min-w-20 sm:px-4", buttonClassName)}
          color="primary"
          variant="flat"
        >
          {buttonText || t("userPages.actions.signIn")}
        </Button>
      </PopoverTrigger>
      {/* Never wider than the screen (minus a margin) on small phones. */}
      <PopoverContent className="w-[300px] max-w-[calc(100vw-1.5rem)] p-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold">
              {t("userPages.actions.signInModal.title")}
            </h3>
            <p className="text-sm text-default-500">
              {t("userPages.actions.signInModal.description")}
            </p>
          </div>
          <div className="space-y-2">
            <OAuthProviderButton
              isLoading={isRedirecting}
              label={t("userPages.actions.signInModal.signInWithGoogle")}
              provider="google"
              onClick={handleGoogleSignIn}
            />
            <OAuthProviderButton
              href={buildKakaoSignInHref(safeRedirectPath)}
              label={t("userPages.actions.signInModal.signInWithKakao")}
              provider="kakao"
              onClick={() => setLanguageCookie(currentLang)}
            />
          </div>
          {hasError && (
            <p className="text-sm text-danger" role="alert">
              {t("account.signInError")}
            </p>
          )}
          <p className="text-xs leading-relaxed text-default-500">
            {richText(t("legal.signInNotice"), {
              terms: (
                <NextLink
                  className={noticeLinkClass}
                  href={`/${currentLang}/terms`}
                  onClick={() => onOpenChange(false)}
                >
                  {t("legal.links.terms")}
                </NextLink>
              ),
              privacy: (
                <NextLink
                  className={noticeLinkClass}
                  href={`/${currentLang}/privacy`}
                  onClick={() => onOpenChange(false)}
                >
                  {t("legal.links.privacy")}
                </NextLink>
              ),
            })}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
